import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const useSocialAuthHandler = () => {
  const { setActiveRole, getUserType } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const handleSocialAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get('access_token');
      const refreshToken = urlParams.get('refresh_token');
      let intendedRole = sessionStorage.getItem('intended_role') as 'client' | 'practitioner' | null;
      if (!intendedRole) {
        intendedRole = urlParams.get('role') as 'client' | 'practitioner' | null;
      }

      if (accessToken && refreshToken) {
        try {
          const { data: { user }, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            console.error('Error setting social auth session:', error);
            toast({ variant: 'destructive', title: 'Authentication Error', description: 'Failed to complete social login. Please try again.' });
            return;
          }

          if (user) {
            // Validate that the user actually has a profile for the intended role
            if (intendedRole) {
              const table = intendedRole === 'practitioner' ? 'practitioners' : 'clients';
              const { data: profileData } = await supabase
                .from(table)
                .select('id')
                .eq('id', user.id)
                .maybeSingle();

              if (!profileData) {
                // Check if they have the opposite role
                const otherTable = intendedRole === 'practitioner' ? 'clients' : 'practitioners';
                const { data: otherProfile } = await supabase
                  .from(otherTable)
                  .select('id')
                  .eq('id', user.id)
                  .maybeSingle();

                if (otherProfile) {
                  // User exists but with wrong role — sign out and show error
                  await supabase.auth.signOut();
                  sessionStorage.removeItem('intended_role');
                  const correctPortal = intendedRole === 'practitioner' ? 'client' : 'practitioner';
                  toast({
                    variant: 'destructive',
                    title: 'Wrong Portal',
                    description: `This account is registered as a ${correctPortal}. Please use the ${correctPortal} login page.`,
                  });
                  const cleanUrl = window.location.pathname;
                  window.history.replaceState({}, document.title, cleanUrl);
                  window.location.href = correctPortal === 'practitioner' ? '/practitioner-auth' : '/client-auth';
                  return;
                }
                // No profile at all — new user, let them through (profile will be created)
              }

              setActiveRole(intendedRole);
            }

            // Decide destination based on intendedRole or current path
            const path = window.location.pathname;
            const target = intendedRole === 'practitioner'
              ? '/practitioner-dashboard'
              : intendedRole === 'client'
                ? '/client-dashboard'
                : path.includes('practitioner')
                  ? '/practitioner-dashboard'
                  : path.includes('client')
                    ? '/client-dashboard'
                    : '/';

            // Clean up and redirect
            sessionStorage.removeItem('intended_role');
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            window.location.href = target;
          }
        } catch (error) {
          console.error('Error handling social auth:', error);
          toast({ variant: 'destructive', title: 'Authentication Error', description: 'Failed to complete social login. Please try again.' });
        }
      }
    };

    handleSocialAuth();
  }, [setActiveRole, toast]);
};

export default useSocialAuthHandler;
